import axios, { AxiosInstance } from 'axios';
import { logger } from './loggerService';
import { invalidateCacheAfterMutation } from '../utils/cacheInvalidation';
import { getApiUrl } from '../config/env';
import { getRequestOrganizationId } from '../utils/organizationRequestContext';
import { logSlowHttpRequest, reportAxiosFailure } from '../utils/observability';

const API_BASE_URL = getApiUrl();

// Utility function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (_error) {
    return true; // If we can't parse the token, consider it expired
  }
};

// Create centralized axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor to add auth token and handle token refresh
api.interceptors.request.use(
  async (config) => {
    // Skip token refresh for auth endpoints
    const isAuthEndpoint =
      config.url?.includes('/auth/login') ||
      config.url?.includes('/auth/register') ||
      config.url?.includes('/auth/verify-email') ||
      config.url?.includes('/auth/resend-verification') ||
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/auth/forgot-password') ||
      config.url?.includes('/auth/reset-password');
    
    if (!isAuthEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token) {
        // Check if token is expired or will expire soon (within 5 minutes)
        if (isTokenExpired(token)) {
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              // Prevent multiple simultaneous refresh attempts
              if (!(config as any)._refreshInProgress) {
                (config as any)._refreshInProgress = true;
                
                const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                  refresh_token: refreshToken,
                });
                const { access_token, refresh_token: newRefreshToken } = response.data.data;
                localStorage.setItem('access_token', access_token);
                if (newRefreshToken) {
                  localStorage.setItem('refresh_token', newRefreshToken);
                }
                (config.headers as any).Authorization = `Bearer ${access_token}`;
                
                (config as any)._refreshInProgress = false;
              } else {
                // Wait for ongoing refresh to complete
                await new Promise(resolve => setTimeout(resolve, 100));
                const newToken = localStorage.getItem('access_token');
                if (newToken) {
                  (config.headers as any).Authorization = `Bearer ${newToken}`;
                }
              }
            } else {
              throw new Error('No refresh token available');
            }
          } catch (_error) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.dispatchEvent(new CustomEvent('auth:logout'));
            if (axios.isAxiosError(_error) && _error.response?.status === 403) {
              window.location.assign('/login');
            }
            return Promise.reject(_error);
          }
        } else {
          (config.headers as any).Authorization = `Bearer ${token}`;
        }
      }
    }

    const orgId = getRequestOrganizationId();
    if (orgId) {
      (config.headers as Record<string, string>)['X-Organization-Id'] = orgId;
      (config.headers as Record<string, string>)['X-Org-Id'] = orgId;
    }

    // Let the browser set multipart boundary — default application/json (or a bare
    // multipart/form-data without boundary) breaks file uploads and often returns 500.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof (config.headers as { delete?: (k: string) => void }).delete === 'function') {
        (config.headers as { delete: (k: string) => void }).delete('Content-Type');
      } else if (config.headers) {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
        delete (config.headers as Record<string, unknown>)['content-type'];
      }
    }

    (config as { __requestStartMs?: number }).__requestStartMs = Date.now();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh, cache invalidation, and errors
api.interceptors.response.use(
  (response) => {
    const cfg = response.config as { __requestStartMs?: number; method?: string; url?: string };
    if (typeof cfg.__requestStartMs === 'number') {
      logSlowHttpRequest(cfg.method?.toUpperCase() || 'GET', cfg.url || '', Date.now() - cfg.__requestStartMs);
    }

    // Invalidate cache after successful mutations (POST, PUT, DELETE)
    const method = response.config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'DELETE'].includes(method)) {
      const endpoint = response.config.url || '';
      
      // Skip cache invalidation for read-only endpoints
      const readOnlyEndpoints = [
        '/comments/counts',  // This is a read operation, not a mutation
        '/auth/refresh',     // Token refresh shouldn't invalidate data cache
        '/auth/logout',      // Logout shouldn't invalidate data cache
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/verify-email',
        '/auth/resend-verification',
      ];
      
      const shouldInvalidate = !readOnlyEndpoints.some(pattern => endpoint.includes(pattern));
      
      if (shouldInvalidate) {
        invalidateCacheAfterMutation(method as 'POST' | 'PUT' | 'DELETE', endpoint);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (originalRequest) {
      const cfg = originalRequest as { __requestStartMs?: number; method?: string; url?: string };
      if (typeof cfg.__requestStartMs === 'number') {
        logSlowHttpRequest(cfg.method?.toUpperCase() || 'GET', cfg.url || '', Date.now() - cfg.__requestStartMs);
      }
    }

    reportAxiosFailure(error);

    // Skip token refresh for auth endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/verify-email') ||
      originalRequest.url?.includes('/auth/resend-verification') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/forgot-password') ||
      originalRequest.url?.includes('/auth/reset-password');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return Promise.reject(new Error('No refresh token available'));
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        
        const { access_token, refresh_token: newRefreshToken } = response.data.data;
        localStorage.setItem('access_token', access_token);
        
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        if (axios.isAxiosError(refreshError) && refreshError.response?.status === 403) {
          window.location.assign('/login');
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/** Thrown by `handleApiError` so callers can distinguish HTTP status (e.g. 401 vs 403). */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'HttpError';
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

// Standardized error handling utility
export const handleApiError = (error: any, context: string): never => {
  let message = 'An unexpected error occurred';
  let status = 0;

  if (axios.isAxiosError(error)) {
    status = error.response?.status ?? 0;
    if (error.response?.data?.error?.message) {
      message = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  const benignCaMembershipAuth403 =
    status === 403 &&
    context === 'AuthService.getCurrentUser' &&
    /active\s+ca\s+firm\s+membership/i.test(message);

  if (benignCaMembershipAuth403) {
    logger.debug(
      'Auth resolved without CA membership (handled via needs_ca_workspace_setup); skipping error log.',
      context
    );
  } else {
    logger.error(message, context, error);
  }

  throw new HttpError(message, status);
};

// API utility functions
export const apiUtils = {
  isTokenExpired,
  handleApiError,
  getApiBaseUrl: () => API_BASE_URL,
};

export { api };


